import { Suspense, useEffect, useState } from "react"
import { ArrowLeft } from "react-feather"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { tenantApi } from "@/api/client/tenant"
import type { TenantUpdateBodyPurpose } from "@/api/generated/models/tenantUpdateBodyPurpose"
import { getErrorMessage } from "@/api/instance"
import { TenantForm, type TenantFormValues } from "@/components/room"
import { toOccupantsFormValues, toOccupantsPayload } from "@/components/room/tenant-form/TenantForm"
import { ErrorPage } from "@/components/ui/error"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Spinner } from "@/components/ui/spinner/Spinner"

export function TenantEditPage() {
  const { tenantId } = useParams<{
    tenantId: string
  }>()
  const navigate = useNavigate()

  if (!tenantId || Number.isNaN(Number(tenantId))) {
    return (
      <ErrorPage
        title="エラー"
        message="賃借人IDが指定されていません。URLを確認してください。"
        backPath="/tenants"
      />
    )
  }

  return (
    <ReactErrorBoundary>
      <Suspense fallback={<TenantEditPageFallback />}>
        <TenantEditPageContent tenantId={tenantId} navigate={navigate} />
      </Suspense>
    </ReactErrorBoundary>
  )
}

interface TenantEditPageContentProps {
  tenantId: string
  navigate: ReturnType<typeof useNavigate>
}

function TenantEditPageContent({ tenantId, navigate }: TenantEditPageContentProps) {
  const location = useLocation()
  const [formKey, setFormKey] = useState(0)

  // テナントデータを取得
  const { data: tenant, refetch } = tenantApi.useTenantGetSuspense(tenantId, {
    withOccupants: "true",
  })

  // tenantUpdateのmutationを取得
  const { mutateAsync: updateTenant, isPending } = tenantApi.useTenantUpdate({
    mutation: {
      onError: (error) =>
        toast.error("賃借人情報の更新に失敗しました", { description: getErrorMessage(error) }),
    },
  })

  // ページに戻ってきた時にデータを再取得
  useEffect(() => {
    const state = location.state as { refetch?: boolean } | null
    if (state?.refetch) {
      refetch().then(() => {
        setFormKey((prev) => prev + 1)
      })
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, refetch, navigate, location.pathname])

  const handleSubmit = async (values: TenantFormValues) => {
    try {
      const requestData = {
        roomId: tenant.tenant.roomId,
        name: values.name,
        nameKana: values.nameKana,
        phone1: values.phone1,
        phone2: values.phone2,
        phone3: values.phone3,
        phone4: values.phone4,
        phone5: values.phone5,
        phone6: values.phone6,
        email: values.email,
        birthday: values.birthday && values.birthday.length > 0 ? values.birthday : null,
        startDate: values.startDate,
        rent: Number(values.rent || 0),
        commonAreaFee: Number(values.commonAreaFee || 0),
        isParking: values.isParking,
        parkingFee: Number(values.parkingFee || 0),
        parkingNumber: values.parkingNumber || "",
        otherFees:
          values.otherFees.length > 0
            ? values.otherFees.map((fee) => ({
                label: fee.name,
                price: Number(fee.baseAmount),
                tax: Number(fee.tax),
              }))
            : null,
        purpose: values.purpose as TenantUpdateBodyPurpose,
        occupants: toOccupantsPayload(values.occupants),
      }

      await updateTenant({
        id: tenantId,
        data: requestData,
      })

      toast.success("賃借人情報を更新しました")
      navigate(`/tenants/${tenantId}`, { state: { refetch: true } })
    } catch {
      // 失敗の表示は mutation の onError が担当する
    }
  }

  const handleCancel = () => {
    navigate(`/tenants/${tenantId}`)
  }

  return (
    <div>
      <div className="flex items-center gap-2 p-6">
        <button
          type="button"
          onClick={() => navigate(`/tenants/${tenantId}`)}
          className="flex items-center justify-center w-7 h-7 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
        >
          <ArrowLeft size={14} className="text-[#354267]" />
        </button>
        <span className="text-sm font-semibold text-[#3f4652]">賃借人詳細に戻る</span>
      </div>

      <div className="max-w-[895px] mx-auto px-6 pb-8">
        <TenantForm
          key={formKey}
          roomId={tenant.tenant.roomId}
          defaultValues={{
            purpose: tenant.tenant.purpose,
            name: tenant.tenant.name,
            nameKana: tenant.tenant.nameKana,
            phone1: tenant.tenant.phone1,
            phone2: tenant.tenant.phone2,
            phone3: tenant.tenant.phone3,
            phone4: tenant.tenant.phone4,
            phone5: tenant.tenant.phone5,
            phone6: tenant.tenant.phone6,
            email: tenant.tenant.email,
            birthday: tenant.tenant.birthday ?? "",
            startDate: tenant.tenant.startDate,
            rent: tenant.tenant.rent?.toString() ?? "",
            commonAreaFee: tenant.tenant.commonAreaFee?.toString() ?? "",
            isParking: tenant.tenant.isParking,
            parkingNumber: tenant.tenant.parkingNumber ?? "",
            parkingFee: tenant.tenant.parkingFee?.toString() ?? "",
            otherFees:
              tenant.tenant.otherFees?.map((fee) => ({
                name: fee.label,
                baseAmount: fee.price.toString(),
                tax: fee.tax.toString(),
              })) ?? [],
            occupants: toOccupantsFormValues(tenant.tenant.occupants),
          }}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isPending}
          submitLabel="保存する"
        />
      </div>
    </div>
  )
}

function TenantEditPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className="text-slate-600 size-10 transition-opacity" />
    </div>
  )
}
